import AvatarUpload from '@/components/filestack/AvatarUpload';
import useDialogLauncher from '@/dialogs-routable/_hooks/useDialogLauncher';
import {DialogManager} from '@/dialogs_new/DialogManager';
import CheckboxField from '@/forms/_fields_v2/CheckboxField';
import SelectField from '@/forms/_fields_v2/SelectField';
import StringField from '@/forms/_fields_v2/StringField';
import TextAreaField from '@/forms/_fields_v2/TextAreaField';
import {useUser} from '@lore/auth';
import {useQueryMutation} from '@lore/query-connect';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useWizard} from '../WizardContext';


export default function AboutYou() {
  const {t} = useTranslation();
  const genderOptions = t('user.demographics.gender.selections', { returnObjects: true });
  const ethnicityOptions = t('user.demographics.ethnicity.selections', { returnObjects: true });

  const genderOptionsFormatted = useMemo(() => ({
    data: genderOptions.map((option) => ({
      id: option.value, data: {name: option.label}
    }))
  }), [genderOptions]);

  const ethnicityOptionsFormatted = useMemo(() => ({
    data: ethnicityOptions.map((option) => ({
      id: option.value, data: {name: option.label}
    }))
  }), [ethnicityOptions]);

  const currentUser = useUser();
  const {
    firstName, lastName, biography, website, linkedinUrl, gender, ethnicity, yearOfBirth, showDemographics
  } = currentUser.data;
  const {registerOnBeforeNext, setNextDisabled} = useWizard();

  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser);
  const launch = useDialogLauncher();

  const [values, setValues] = useState({
    firstName, lastName, biography, website, linkedinUrl, gender, ethnicity, yearOfBirth, showDemographics
  });

  function set(field) {
    return (value) => setValues(v => ({...v, [field]: value}));
  }

  useEffect(() => {
    setNextDisabled(!values.biography?.trim());
  }, [values.biography, setNextDisabled]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      setNextDisabled(true);
      try {
        await userMutation.mutateAsync(values);
        return true;
      } catch {
        return false;
      } finally {
        setNextDisabled(false);
      }
    });
  }, [values, registerOnBeforeNext, setNextDisabled, userMutation]);

  return (<div className="flex flex-col px-6 py-10 space-y-10 max-h-lvh md:max-h-[70lvh] overflow-y-auto">
      <div className="text-3xl font-bold mx-auto">About you</div>
      <div className="mx-auto">Tell us about yourself! Share a few details to help our community get to know you and
        connect more easily.
      </div>
      <div className="w-full p-4 bg-gray-50 rounded-2xl border border-gray-300 flex flex-col md:flex-row gap-10 items-start">
        <AvatarUpload
          description="Adding a photo to your profile helps you get recognized and makes our community a warmer, friendlier place."/>
        <div className="shrink-0 space-y-4">
          <TextAreaField
            label={(<span>
                <span className="text-red-600">* </span>In 1-2 sentences how would you describe yourself?{' '}
                <button
                  type="button"
                  className="text-cs-orange-500 hover:underline"
                  onClick={() => launch(DialogManager.BiographySuggestions.Config())}
                >
                  Need an example?
                </button>
              </span>)}
            value={values.biography}
            onChange={set('biography')}
            rows={3}
          />
          <StringField
            label="Website"
            value={values.website}
            onChange={set('website')}
          />

          <StringField
            label="LinkedIn"
            value={values.linkedinUrl}
            onChange={set('linkedinUrl')}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="font-bold">If you’re comfortable, please participate in our demographic survey:</div>
        <div className="text-base text-gray-500">This information helps us better understand and support the communities
          we work with - this won’t be displayed on your profile unless you give your permission.
        </div>
        <SelectField
          value={values.gender}
          placeholder="Select"
          options={genderOptionsFormatted}
          optionLabel="name"
          label="Gender identity"
          onChange={set('gender')}
        />
        <SelectField
          value={values.ethnicity}
          placeholder="Select"
          options={ethnicityOptionsFormatted}
          optionLabel="name"
          label="Ethnicity"
          onChange={set('ethnicity')}
        />
        <StringField
          label="Year of birth"
          value={values.yearOfBirth}
          placeholder="Input answer"
          onChange={set('yearOfBirth')}
        />
        <CheckboxField
          label="I agree to participate in the demographic survey"
          checked={values.showDemographics}
          onChange={(event) => set('showDemographics')(event.target.checked)}
        />
      </div>
    </div>);
}
